/**
 * 텍스트 방향 — LTR(좌→우, 라틴/한글/CJK) 또는 RTL(우→좌, 아랍/히브리).
 * CTL(Complex Text Layout)은 RTL + Bidi reorder(`./bidi.ts`)의 조합으로 흡수하며 별도 값을 두지 않는다.
 */
export type TextDirection = "ltr" | "rtl";

/**
 * RTL(우→좌) 문자를 쓰는 언어코드 집합 — 방향 판정의 SSoT.
 * ISO 639-1 primary subtag 기준. 새 RTL 언어 지원 시 여기에 추가하면 `resolveDirection`이 자동 반영.
 */
export const RTL_LOCALES: ReadonlySet<string> = new Set<string>([
  "ar", // Arabic 아랍어
  "he", // Hebrew 히브리어
  "fa", // Persian/Farsi 페르시아어
  "ur", // Urdu 우르두어
  "ps", // Pashto 파슈토어
  "sd", // Sindhi 신디어
  "yi", // Yiddish 이디시어
  "dv", // Dhivehi/Maldivian 디베히어
]);

/**
 * 언어코드 또는 명시 방향으로 텍스트 방향을 결정한다.
 * - `dir`이 명시되면 그대로 사용(locale보다 우선).
 * - `locale`은 BCP-47 허용 — `"ar-EG"`/`"he_IL"`처럼 region subtag가 붙어도 첫 subtag만 보고 매칭.
 * - 미매칭·미지정이면 `"ltr"`.
 */
export function resolveDirection(input: { dir?: TextDirection; locale?: string }): TextDirection {
  if (input.dir) return input.dir;
  const locale = input.locale;
  if (!locale) return "ltr";
  const primary = locale.toLowerCase().split(/[-_]/)[0] ?? "";
  return RTL_LOCALES.has(primary) ? "rtl" : "ltr";
}

/** 방향이 RTL인지. */
export function isRtl(dir: TextDirection): boolean {
  return dir === "rtl";
}
